import api from './api';
import { ApiResponse, Questionnaire, QuestionnaireRequest, PageResponse } from '../types';

export const questionnaireService = {
  getQuestionnaires: async (params: {
    schoolId?: number;
    search?: string;
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: 'ASC' | 'DESC';
  }): Promise<PageResponse<Questionnaire>> => {
    const response = await api.get<ApiResponse<PageResponse<Questionnaire>>>('/admin/questionnaires', {
      params: {
        schoolId: params.schoolId,
        search: params.search || undefined,
        page: params.page || 0,
        size: params.size || 10,
        sortBy: params.sortBy || 'createdAt',
        sortDir: params.sortDir || 'DESC',
      },
    });
    return response.data.data;
  },

  getQuestionnaire: async (id: number): Promise<Questionnaire> => {
    const response = await api.get<ApiResponse<Questionnaire>>(`/admin/questionnaires/${id}`);
    return response.data.data;
  },

  createQuestionnaire: async (questionnaire: QuestionnaireRequest): Promise<Questionnaire> => {
    const response = await api.post<ApiResponse<Questionnaire>>('/admin/questionnaires', questionnaire);
    return response.data.data;
  },

  updateQuestionnaire: async (id: number, questionnaire: Partial<QuestionnaireRequest>): Promise<Questionnaire> => {
    const response = await api.put<ApiResponse<Questionnaire>>(`/admin/questionnaires/${id}`, questionnaire);
    return response.data.data;
  },

  deleteQuestionnaire: async (id: number): Promise<void> => {
    await api.delete(`/admin/questionnaires/${id}`);
  },

  publishQuestionnaire: async (id: number): Promise<Questionnaire> => {
    const response = await api.post<ApiResponse<Questionnaire>>(`/admin/questionnaires/${id}/publish`);
    return response.data.data;
  },

  disableQuestionnaire: async (id: number): Promise<Questionnaire> => {
    const response = await api.post<ApiResponse<Questionnaire>>(`/admin/questionnaires/${id}/disable`);
    return response.data.data;
  },

  enableQuestionnaire: async (id: number): Promise<Questionnaire> => {
    const response = await api.post<ApiResponse<Questionnaire>>(`/admin/questionnaires/${id}/enable`);
    return response.data.data;
  },

  markVersion: async (id: number): Promise<Questionnaire> => {
    const response = await api.post<ApiResponse<Questionnaire>>(`/admin/questionnaires/${id}/mark-version`);
    return response.data.data;
  },

  getDeletedQuestionnaires: async (params: {
    search?: string;
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: 'ASC' | 'DESC';
  }): Promise<PageResponse<Questionnaire>> => {
    const response = await api.get<ApiResponse<PageResponse<Questionnaire>>>('/admin/questionnaires/trash', {
      params: {
        search: params.search || undefined,
        page: params.page || 0,
        size: params.size || 10,
        sortBy: params.sortBy || 'updatedAt',
        sortDir: params.sortDir || 'DESC',
      },
    });
    return response.data.data;
  },

  restoreQuestionnaire: async (id: number): Promise<Questionnaire> => {
    const response = await api.post<ApiResponse<Questionnaire>>(`/admin/questionnaires/${id}/restore`);
    return response.data.data;
  },

  permanentlyDeleteQuestionnaire: async (id: number): Promise<void> => {
    await api.delete(`/admin/questionnaires/${id}/permanent`);
  },

  duplicateQuestionnaire: async (id: number, title?: string): Promise<Questionnaire> => {
    const response = await api.post<ApiResponse<Questionnaire>>(
      `/admin/questionnaires/${id}/duplicate`,
      title ? { title } : {}
    );
    return response.data.data;
  },
};
